import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

function siteHomepageUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}

/** Default closing for every broadcast — homepage link + signature. */
export function defaultBroadcastBody(homeUrl = siteHomepageUrl()) {
  const home = (homeUrl || '').replace(/\/$/, '');
  return `\n\n${home}\n\nKisses,\n\nCoffey`;
}

/** Formats an array of email strings as comma + space delimited string. */
export function formatEmailList(emails) {
  if (!Array.isArray(emails)) return '';
  return emails.filter(Boolean).map(e => String(e).trim()).filter(Boolean).join(', ');
}

/** Builds Gmail compose web URL with prefilled subject and body. */
export function buildGmailComposeUrl(subject = '', body = '') {
  const params = new URLSearchParams();
  params.set('view', 'cm');
  params.set('fs', '1');
  if (subject && subject.trim()) params.set('su', subject.trim());
  if (body && body.trim()) params.set('body', body.trim());
  return `https://mail.google.com/mail/?${params.toString()}`;
}

function userDisplayName(u) {
  const name = `${(u.first_name || '').trim()} ${(u.last_name || '').trim()}`.trim();
  return name || u.email || u.id;
}

export default function AdminBroadcast({ token, me }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(() => defaultBroadcastBody());
  const [status, setStatus] = useState('');
  const [failedRecipients, setFailedRecipients] = useState([]);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [failedCopyFeedback, setFailedCopyFeedback] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  // Preset highlight only: 'all' | 'active' | 'unpaid' | 'noEntries' | 'noPickCurrentWeek' | null
  const [activePreset, setActivePreset] = useState(null);
  const [users, setUsers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [currentWeekId, setCurrentWeekId] = useState(null);
  const [weekPicks, setWeekPicks] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [activeSeason, setActiveSeason] = useState(null);
  const [rosterReady, setRosterReady] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setRosterReady(false);
      try {
        const seasonsRes = await fetch(`${API_BASE}/api/seasons`);
        if (!seasonsRes.ok) throw new Error('Failed to load seasons');
        const seasonsData = await seasonsRes.json().catch(() => ({}));
        const seasonYear = seasonsData && seasonsData.active_season != null ? seasonsData.active_season : null;
        if (seasonYear == null) {
          setActiveSeason(null);
          setUsers([]);
          setEntries([]);
          setError('No active season set — presets cannot be computed safely. Set an active season in Admin Config.');
          setRosterReady(false);
          return;
        }
        setActiveSeason(seasonYear);
        setError('');

        const entriesUrl = `${API_BASE}/api/admin/entries?season=${encodeURIComponent(seasonYear)}`;
        const [usersRes, entriesRes] = await Promise.all([
          fetch(`${API_BASE}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(entriesUrl, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (!usersRes.ok || !entriesRes.ok) throw new Error('Failed to load users or entries');
        const usersData = await usersRes.json();
        const entriesData = await entriesRes.json();
        setUsers(Array.isArray(usersData) ? usersData : []);
        setEntries(Array.isArray(entriesData) ? entriesData : []);
        setRosterReady(true);
      } catch (e) {
        setError(e.message || 'Failed to load users or entries');
        setRosterReady(false);
      }
    };
    fetchData();
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const wRes = await fetch(`${API_BASE}/api/weeks`);
        const wList = await wRes.json().catch(() => []);
        const cur = Array.isArray(wList) ? wList.find(w => w && w.is_current) : null;
        const wid = cur ? cur.id : null;
        if (!cancelled) setCurrentWeekId(wid);
        if (wid) {
          try {
            const pRes = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(wid)}/picks`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const pData = await pRes.json().catch(() => []);
            if (!cancelled) setWeekPicks(Array.isArray(pData) ? pData : []);
          } catch {
            if (!cancelled) setWeekPicks([]);
          }
        } else if (!cancelled) {
          setWeekPicks([]);
        }
      } catch {
        if (!cancelled) {
          setCurrentWeekId(null);
          setWeekPicks([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const annotatedUsers = useMemo(() => {
    const userMap = new Map();
    const entriesByUser = new Map();
    for (const e of entries) {
      if (!e || !e.user_id) continue;
      if (!userMap.has(e.user_id)) userMap.set(e.user_id, { active: false, unpaid: false, entryCount: 0 });
      if (!entriesByUser.has(e.user_id)) entriesByUser.set(e.user_id, []);
      entriesByUser.get(e.user_id).push(e);
      const info = userMap.get(e.user_id);
      info.entryCount++;
      if (!e.is_eliminated) info.active = true;
      if (!e.is_paid) info.unpaid = true;
    }
    const list = users.map(u => {
      const entryInfo = userMap.get(u.id) || { active: false, unpaid: false, entryCount: 0 };
      return { ...u, ...entryInfo, displayName: userDisplayName(u), _entries: entriesByUser.get(u.id) || [] };
    });
    list.sort((a, b) => a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()));
    return list;
  }, [users, entries]);

  const idsForPreset = (preset, userList, picks) => {
    if (preset === 'all') return userList.map(u => u.id);
    if (preset === 'active') return userList.filter(u => u.active).map(u => u.id);
    if (preset === 'unpaid') return userList.filter(u => u.unpaid).map(u => u.id);
    if (preset === 'noEntries') return userList.filter(u => u.entryCount === 0).map(u => u.id);
    if (preset === 'noPickCurrentWeek') {
      const pickedEntryIds = new Set((picks || []).filter(p => p && !p.no_pick).map(p => p.entry_id));
      return userList
        .filter(u => u._entries.some(e => !e.is_eliminated && !pickedEntryIds.has(e.id)))
        .map(u => u.id);
    }
    return [];
  };

  // Keep preset selection in sync when roster/picks finish loading after a preset click.
  useEffect(() => {
    if (!activePreset || !rosterReady) return;
    if (activePreset === 'noPickCurrentWeek' && !currentWeekId) return;
    setSelectedIds(new Set(idsForPreset(activePreset, annotatedUsers, weekPicks)));
  }, [activePreset, annotatedUsers, weekPicks, rosterReady, currentWeekId]);

  const applyPreset = (preset) => {
    setActivePreset(preset);
    if (!rosterReady) return;
    setSelectedIds(new Set(idsForPreset(preset, annotatedUsers, weekPicks)));
  };

  const clearSelection = () => {
    setActivePreset(null);
    setSelectedIds(new Set());
  };

  const toggleUser = (id) => {
    setActivePreset(null);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return annotatedUsers;
    return annotatedUsers.filter(u => {
      const hay = `${u.displayName} ${u.email || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [annotatedUsers, search]);

  const selectedEmails = useMemo(() => {
    return annotatedUsers
      .filter(u => selectedIds.has(u.id) && u.email)
      .map(u => u.email.trim())
      .filter(Boolean);
  }, [annotatedUsers, selectedIds]);

  const gmailUrl = useMemo(() => {
    return buildGmailComposeUrl(subject, body);
  }, [subject, body]);

  const copyToClipboard = async (text, onSuccess) => {
    try {
      if (!text) return;
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      if (onSuccess) onSuccess();
    } catch (e) {
      setError('Failed to copy to clipboard: ' + (e.message || ''));
    }
  };

  const copyEmails = async () => {
    const text = formatEmailList(selectedEmails);
    await copyToClipboard(text, () => {
      setCopyFeedback('Copied!');
      setTimeout(() => setCopyFeedback(''), 2500);
    });
  };

  const exportEmailListCsv = () => {
    try {
      const text = formatEmailList(selectedEmails);
      if (!text) return;
      const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `broadcast_recipients_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to export CSV: ' + (e.message || ''));
    }
  };

  if (!token) return <Navigate to="/auth" replace />;
  if (me == null) return <div className="mx-auto max-w-7xl px-4 py-6"><div>Loading…</div></div>;
  if (me && !me.is_admin) return <div className="mx-auto max-w-7xl px-4 py-6"><h2>Access denied</h2><Link to="/">Return to Dashboard</Link></div>;

  const selectedCount = selectedIds.size;
  const canSend = !!subject.trim() && !!body.trim() && selectedCount > 0 && !sending;
  const presetsDisabled = !rosterReady;

  const send = async () => {
    if (!canSend) return;
    if (!window.confirm(`Send to ${selectedCount} recipient${selectedCount === 1 ? '' : 's'}?`)) return;
    setError('');
    setStatus('');
    setFailedRecipients([]);
    setSending(true);
    try {
      const payload = {
        subject: subject.trim(),
        text: body.trim(),
        recipient_ids: Array.from(selectedIds),
      };
      const res = await fetch(`${API_BASE}/api/admin/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      setStatus(`Sent to ${data.sent || 0} recipients`);
      if (Array.isArray(data.failed) && data.failed.length > 0) {
        setFailedRecipients(data.failed);
      }
      setSubject('');
      setBody(defaultBroadcastBody());
      clearSelection();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const presetBtnStyle = (key) => ({
    background: activePreset === key ? 'var(--win-color)' : 'var(--secondary)',
    color: activePreset === key ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
    border: '1px solid var(--win-color)',
    borderRadius: 4,
    padding: '4px 12px',
    opacity: presetsDisabled ? 0.5 : 1,
  });

  const toolBtnStyle = {
    background: 'var(--secondary)',
    color: 'var(--primary)',
    border: '1px solid var(--border)',
    borderRadius: 4,
    padding: '4px 10px',
    fontSize: 13,
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 text-left">
      <h1 className="mb-2 text-2xl font-semibold tracking-tight">Admin Broadcast</h1>
      <p className="mb-4 text-sm text-muted-foreground">Connected to: <code>{API_BASE}</code></p>
      <div style={{ marginBottom: 8 }}>
        <Link to="/admin">← Back to Admin Dashboard</Link>
      </div>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Compose</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-2 text-sm text-muted-foreground">
            Compose a message, select recipients, then send.
            {activeSeason != null ? (
              <span> Presets use active season {activeSeason}.</span>
            ) : null}
          </div>
          {error && <div style={{ color: 'tomato', marginBottom: 8 }}>{error}</div>}
          {status && <div role="status" aria-live="polite" style={{ color: 'var(--win-color)', marginBottom: 8 }}>{status}</div>}
          {failedRecipients.length > 0 && (
            <div style={{ background: 'rgba(255, 99, 71, 0.1)', border: '1px solid tomato', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
              <div style={{ color: 'tomato', fontWeight: 600, marginBottom: 4 }}>
                Warning: {failedRecipients.length} recipient{failedRecipients.length === 1 ? '' : 's'} failed to send:
              </div>
              <div style={{ fontSize: 13, wordBreak: 'break-all', marginBottom: 6 }}>
                {formatEmailList(failedRecipients)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => {
                    copyToClipboard(formatEmailList(failedRecipients), () => {
                      setFailedCopyFeedback('Copied!');
                      setTimeout(() => setFailedCopyFeedback(''), 2500);
                    });
                  }}
                  style={{ ...toolBtnStyle, fontSize: 12, padding: '2px 8px' }}
                >
                  Copy Failed Emails
                </button>
                {failedCopyFeedback && <span style={{ color: 'var(--win-color)', fontSize: 12 }}>{failedCopyFeedback}</span>}
              </div>
            </div>
          )}
          <div style={{ display: 'grid', gap: 8 }}>
            <input placeholder="Subject" value={subject} onChange={e => setSubject(e.target.value)} />
            <textarea placeholder="Message body" rows={8} value={body} onChange={e => setBody(e.target.value)} />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0' }}>
              <button type="button" style={presetBtnStyle('all')} disabled={presetsDisabled} onClick={() => applyPreset('all')}>All Users</button>
              <button type="button" style={presetBtnStyle('active')} disabled={presetsDisabled} onClick={() => applyPreset('active')}>Active</button>
              <button type="button" style={presetBtnStyle('unpaid')} disabled={presetsDisabled} onClick={() => applyPreset('unpaid')}>Unpaid</button>
              <button type="button" style={presetBtnStyle('noEntries')} disabled={presetsDisabled} onClick={() => applyPreset('noEntries')}>No Entries</button>
              <button
                type="button"
                style={presetBtnStyle('noPickCurrentWeek')}
                onClick={() => applyPreset('noPickCurrentWeek')}
                disabled={presetsDisabled || !currentWeekId}
                title={currentWeekId ? undefined : 'No current week set'}
              >Active w/o pick (current)</button>
              <button
                type="button"
                onClick={clearSelection}
                style={{ background: 'var(--secondary)', color: 'var(--muted-foreground)', border: '1px solid var(--border)', borderRadius: 4, padding: '4px 12px' }}
              >Clear</button>
            </div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Presets replace the selection. Check or uncheck individuals afterward. Search only hides rows.
            </div>

            <div style={{ margin: '8px 0', padding: '8px', background: 'var(--secondary)', borderRadius: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 6 }}>
                <strong>Recipients ({selectedCount} selected of {annotatedUsers.length}):</strong>
                {selectedCount > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <button type="button" onClick={copyEmails} style={toolBtnStyle} title="Copy selected emails as comma-separated list">
                      📋 Copy Emails
                    </button>
                    {copyFeedback && <span style={{ color: 'var(--win-color)', fontSize: 12 }}>{copyFeedback}</span>}
                    <button type="button" onClick={exportEmailListCsv} style={toolBtnStyle} title="Export selected emails to CSV file">
                      📥 Export List
                    </button>
                    <a
                      href={gmailUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={toolBtnStyle}
                      title="Open compose window in Gmail with subject and body prefilled"
                    >
                      ✉️ Open in Gmail
                    </a>
                  </div>
                )}
              </div>
              <input
                type="search"
                placeholder="Search name or email"
                value={search}
                onChange={e => setSearch(e.target.value)}
                aria-label="Search recipients"
                style={{ width: '100%', marginTop: 4, marginBottom: 8, boxSizing: 'border-box' }}
              />
              <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', maxHeight: 360, overflowY: 'auto' }}>
                {visibleUsers.map(u => {
                  const checked = selectedIds.has(u.id);
                  const inputId = `broadcast-user-${u.id}`;
                  return (
                    <li key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <label
                        htmlFor={inputId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 4px',
                          cursor: 'pointer',
                          minHeight: 40,
                        }}
                      >
                        <input
                          id={inputId}
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleUser(u.id)}
                          aria-label={`Select ${u.displayName}`}
                        />
                        <span>
                          {u.displayName}
                          {u.email ? <span style={{ opacity: 0.7 }}> {u.email}</span> : null}
                        </span>
                      </label>
                    </li>
                  );
                })}
                {visibleUsers.length === 0 && (
                  <li style={{ padding: '8px 4px', opacity: 0.7 }}>
                    {rosterReady ? 'No users match the search.' : 'Loading users…'}
                  </li>
                )}
              </ul>
            </div>

            <button onClick={send} disabled={!canSend}>
              {sending ? 'Sending…' : `Send Broadcast${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
